'use client';

import { useEffect, useRef, useCallback } from 'react';

/**
 * React wrapper for the ALTCHA Web Component.
 *
 * Loads the ALTCHA widget from a self-hosted, same-origin script
 * (public/vendor/altcha.js), then creates the <altcha-widget> element
 * imperatively.
 *
 * - Solves the PoW challenge in a Web Worker (off-main-thread)
 * - Calls onVerified with the base64-encoded payload when done
 * - Invisible — no UI rendered
 */

export interface AltchaErrorDetail {
	// 'init' covers both CDN script-load failures and widget setup failures —
	// both reject the same ensureAltchaLoaded() promise chain.
	stage: 'init' | 'verification';
	error?: unknown;
}

interface AltchaWidgetProps {
	onVerified: (payload: string) => void;
	onError?: (detail: AltchaErrorDetail) => void;
}

// Singleton: only load the script once
let scriptLoaded = false;
let scriptLoadPromise: Promise<void> | null = null;

const MAX_LOAD_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

function loadScriptOnce(): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		const script = document.createElement('script');
		// Self-hosted (public/vendor/altcha.js, vendored from node_modules by
		// scripts/copy-altcha.js) instead of a third-party CDN — a failed CDN
		// load was blocking form submission for real users (e.g. mobile Safari).
		script.src = '/vendor/altcha.js';
		script.async = true;
		script.onload = () => {
			scriptLoaded = true;
			resolve();
		};
		script.onerror = (err) => {
			script.remove();
			reject(err);
		};
		document.head.appendChild(script);
	});
}

function ensureAltchaLoaded(): Promise<void> {
	// If customElements already has altcha-widget, we're good
	if (typeof customElements !== 'undefined' && customElements.get('altcha-widget')) {
		return Promise.resolve();
	}

	if (scriptLoaded) return Promise.resolve();
	if (scriptLoadPromise) return scriptLoadPromise;

	// Retry a few times before giving up — a same-origin script request can
	// still fail transiently (mobile network blip), and that shouldn't
	// permanently block form submission for the rest of the page's lifetime.
	scriptLoadPromise = (async () => {
		let lastError: unknown;
		for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt++) {
			try {
				await loadScriptOnce();

				return;
			} catch (err) {
				lastError = err;
				console.error(`[AltchaWidget] Script load attempt ${attempt}/${MAX_LOAD_ATTEMPTS} failed`, err);
				if (attempt < MAX_LOAD_ATTEMPTS) {
					await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
				}
			}
		}
		// Let a later mount try again from scratch instead of staying stuck.
		scriptLoadPromise = null;
		throw lastError;
	})();

	return scriptLoadPromise;
}

export default function AltchaWidget({ onVerified, onError }: AltchaWidgetProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const widgetRef = useRef<HTMLElement | null>(null);

	const handleStateChange = useCallback(
		(ev: Event) => {
			const detail = (ev as CustomEvent).detail;
			if (detail?.state === 'verified' && detail?.payload) {
				onVerified(detail.payload);
			}
			if (detail?.state === 'error') {
				console.error('[AltchaWidget] Verification error:', detail);
				onError?.({ stage: 'verification', error: detail });
			}
		},
		[onVerified, onError],
	);

	const handleVerified = useCallback(
		(ev: Event) => {
			const detail = (ev as CustomEvent).detail;
			if (detail?.payload) {
				onVerified(detail.payload);
			}
		},
		[onVerified],
	);

	useEffect(() => {
		let mounted = true;

		ensureAltchaLoaded()
			.then(() => {
				if (!mounted || !containerRef.current || widgetRef.current) return;

				const widget = document.createElement('altcha-widget');
				widget.setAttribute('challenge', `${window.location.origin}/api/altcha/challenge`);
				widget.setAttribute('auto', 'onload');
				widget.setAttribute('hidefooter', 'true');
				widget.setAttribute('hidelogo', 'true');
				widget.setAttribute('name', 'altcha');
				widget.style.position = 'absolute';
				widget.style.left = '-9999px';

				widget.addEventListener('statechange', handleStateChange);
				widget.addEventListener('verified', handleVerified);
				containerRef.current.appendChild(widget);
				widgetRef.current = widget;

				return undefined;
			})
			.catch((err) => {
				console.error('[AltchaWidget] Failed to initialize:', err);
				onError?.({ stage: 'init', error: err });
			});

		return () => {
			mounted = false;
			if (widgetRef.current) {
				widgetRef.current.removeEventListener('statechange', handleStateChange);
				widgetRef.current.removeEventListener('verified', handleVerified);
			}
		};
	}, [handleStateChange, handleVerified, onError]);

	return <div ref={containerRef} />;
}
